import { useState, useEffect, useContext } from "react";

import { Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";

import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import PortalDropdown from "../../components/PortalDropdown";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";
import classes from "../../components/Modal.module.css";
import BackToDashboard from "../../components/BackToDashboard";
import SectionDivider from "../../components/SectionDivider";
import BankAccountSection from "../merchant-pages/BankAccountSection";
import ErrorBox from "../../components/ErrorBox";
import AreYouSureModal from "../../components/AreYouSureModal";
import { shouldCloseModal } from "../../utils/shouldCloseModal";
import UploadFiles from "../../components/UploadFiles";
import { uploadSupplierDocuments } from "../../services/suppliers/suppliersServices";
import PDFViewer from "../../components/PDFViewer";
import ConfirmationBox from "../../components/ConfirmationBox";
import PageWrapper from "../../components/PageWrapper";

export default function SuppliersPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  // const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  // const [networkError, setNetworkError] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [updateSupplierModal, setUpdateSupplierModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updateSupplierFields, setUpdateSupplierFields] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1); // Added pagination state
  const [totalPages, setTotalPages] = useState(1); // Added totalPages state
  const [totalRecords, setTotalRecords] = useState(0); // Added totalRecords state
  const [loadingModal, setLoadingModal] = useState(false);
  const [addBankAccountFields, setAddBankAccountFields] = useState(false);
  const [duplicateAccountNumber, setDuplicateAccountNumber] = useState("");
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const [showUpdateAreYouSure, setShowUpdateAreYouSure] = useState(false);

  const [creationStep, setCreationStep] = useState(1); // 1 = data entry, 2 = file upload
  const [newlyCreatedSupplierId, setNewlyCreatedSupplierId] = useState(null);
  const [uploadFiles, setUploadFiles] = useState({
    supplierCertificate: null,
    articlesOfAssociation: null,
  });

  const [supplierFiles, setSupplierFiles] = useState({
    supplierCertificate: null,
    articlesOfAssociation: null,
  });

  const { userData } = useContext(UserAuth);

  const {
    handleShowSupplierModal,
    error,
    setError,
    currencies,
    supplierCurrency,
    setSupplierCurrency,
    supplierModal,
    setSupplierModal,
    handleSaveSupplier,
    supplierFields,
    setSupplierFields,
    suppliers,
    setSuppliers,
    loading,
    setLoading,
  } = useContext(DataContext);

  useEffect(() => {
    getSuppliers();
  }, [currentPage, searchTerm, userData]);

  useEffect(() => {
    setFilteredSuppliers(
      suppliers?.filter((supplier) =>
        supplier?.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, suppliers]);

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;
  const emptyInputField = error === "emptyInputFields" ? "emptyInputField" : "";

  const getSuppliers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getSuppliers`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            "User-Id": parseInt(userData?.userId),
            "organization-Id": parseInt(userData?.organizationId),
          },
          params: {
            page: currentPage,
            limit: 5,
            search: searchTerm,
          },
        }
      );

      if (response?.status === 200) {
        setLoading(false);
      }

      if (response.data && response.data.suppliers) {
        setSuppliers(response.data.suppliers);
        setFilteredSuppliers(response.data.suppliers);
        setTotalPages(response.data.totalPages);
        setTotalRecords(response.data.totalRecords);
      } else {
        console.error("Failed to get the suppliers");
      }
    } catch (error) {
      // setNetworkError(true);
      console.error("Failed to get the suppliers", error);
      setLoading(false);
    }
  };

  const handleAttemptCancel = () => {
    const hasData = shouldCloseModal({ ...supplierFields, bankAccounts: null });
    if (hasData) {
      handleCancel("newSupplier");
      return;
    } else {
      setShowAreYouSure(true);
    }
  };

  const openUpdateSupplierModal = (supplier) => {
    setSelectedSupplier(supplier);
    setUpdateSupplierFields({
      supplierName: supplier.name,
      supplierCompany: supplier.company,
      supplierWebsite: supplier.website,
      supplierAddress: supplier.address,
      supplierCountry: supplier.country,
      supplierCurrency: supplier.currency,
      supplierCity: supplier.city,
      supplierPhoneNumber: supplier.phoneNumber,

      supplierEmail: supplier.email,
      supplierGoodsType: supplier.typeOfGoods,
      supplierBusinessSector: supplier.businessSector,
      supplierCountryCode: supplier.countryCode,
      supplierRelationWithSupplier: supplier.relationWithSupplier,
    }); // Initialize fields for editing

    const existingDocs = {
      supplierCertificate: supplier.documents?.find(
        (doc) => doc.name === "supplierCertificate"
      ),
      articlesOfAssociation: supplier.documents?.find(
        (doc) => doc.name === "articleOfAssociation"
      ),
    };
    setSupplierFiles(existingDocs);

    setIsEditing(false); // Set to editing mode
    setUpdateSupplierModal(true);
  };

  const handleInputChange = (e, state) => {
    if (state === "supplier") {
      setSupplierFields({
        ...supplierFields,
        [e.target.name]: e.target.value,
      });
    }
    setError("");
  };

  const handleUpdateInputChange = (e, fieldName) => {
    setUpdateSupplierFields({
      ...updateSupplierFields,
      [fieldName]: e.target.value,
    });
  };

  const handleAttemptCancelUpdate = () => {
    const hasData = shouldCloseModal(updateSupplierFields);
    if (hasData) {
      handleCancel();
      return;
    } else {
      setShowUpdateAreYouSure(true);
    }
  };

  const handleUpdateSupplier = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);

    try {
      setLoadingModal(true);
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/updateSupplier/${selectedSupplier.id}`,
        updateSupplierFields,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
          },
        }
      );

      if (response.data.message === "Supplier updated successfully") {
        await handleUpdateFiles();
        await getSuppliers(); // Refetch suppliers to get updated data
        setUpdateSupplierModal(false);
      } else {
        console.error("Failed to update supplier:", response.data.message);
      }
      setLoadingModal(false);
    } catch (error) {
      const message = error?.response?.data?.message;
      const accountNumber = error?.response?.data?.accountNumber;

      setError(message || "unexpectedError");
      setDuplicateAccountNumber(accountNumber);
      setLoadingModal(false);
      console.error("Error updating supplier:", error);
    }
  };

  const handleAddSupplier = async () => {
    setLoadingModal(true);
    const response = await handleSaveSupplier();
    if (response && response.data && response.data.newSupplier) {
      // Store the new supplier ID and move to file upload step
      setNewlyCreatedSupplierId(response.data.newSupplier.id);
      setCreationStep(2);
      setAddBankAccountFields(false);
      setError("");
      // await getSuppliers();
    }
    setLoadingModal(false);
  };

  const handleCancel = (modal) => {
    setShowAreYouSure(false);
    setSupplierModal(false);
    setUpdateSupplierModal(false);
    setSelectedSupplier(null);
    setAddBankAccountFields(false);
    setError("");
    setDuplicateAccountNumber("");
    setShowUpdateAreYouSure(false);

    setSupplierFiles({
      supplierCertificate: null,
      articlesOfAssociation: null,
    });

    // Add these new lines
    setCreationStep(1);
    setNewlyCreatedSupplierId(null);
    setUploadFiles({
      supplierCertificate: null,
      articlesOfAssociation: null,
    });

    if (modal === "newSupplier") {
      setSupplierFields({
        supplierName: "",
        supplierCompany: "",
        supplierAddress: "",
        supplierCountry: "",
        supplierWebsite: "",
        supplierCurrency: "",
        supplierCity: "",
        supplierPhoneNumber: "",
        bankAccounts: [],
        supplierEmail: "",
        supplierGoodsType: "",
        supplierBusinessSector: "",
        supplierCountryCode: "",
        supplierRelationWithSupplier: "",
      });
    }
  };

  const handleAddBankAccount = () => {
    setAddBankAccountFields(true);
  };

  const handleBankAccount = (bankAccounts) => {
    setSupplierFields((prev) => ({ ...prev, bankAccounts }));
  };

  const handleEditBankAccount = (bankAccounts) => {
    setUpdateSupplierFields((prev) => ({ ...prev, bankAccounts }));
  };

  const handleFileChange = (file, fieldName) => {
    setUploadFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    if (error) setError("");
  };

  const handleDeleteFile = (fieldName) => {
    setUploadFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
  };

  const handleUploadFiles = async () => {
    try {
      setLoadingModal(true);
      const formData = new FormData();

      if (uploadFiles.supplierCertificate) {
        formData.append("supplierCertificate", uploadFiles.supplierCertificate);
      }

      if (uploadFiles.articlesOfAssociation) {
        formData.append(
          "articleOfAssociation",
          uploadFiles.articlesOfAssociation
        );
      }

      const response = await uploadSupplierDocuments(
        newlyCreatedSupplierId,
        formData
      );

      // Success - close modal and reset
      if (response.status == 200) {
        handleCompleteCreation();
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      setError("errorUploadingFiles");
    } finally {
      setLoadingModal(false);
    }
  };

  const handleUpdateFiles = async () => {
    try {
      setLoadingModal(true);
      const formData = new FormData();
      const existingFiles = [];

      // Add new files if they are File objects
      if (supplierFiles.supplierCertificate instanceof File) {
        formData.append(
          "supplierCertificate",
          supplierFiles.supplierCertificate
        );
      } else if (supplierFiles.supplierCertificate) {
        // Keep existing file
        existingFiles.push("supplierCertificate");
      }

      if (supplierFiles.articlesOfAssociation instanceof File) {
        formData.append(
          "articleOfAssociation",
          supplierFiles.articlesOfAssociation
        );
      } else if (supplierFiles.articlesOfAssociation) {
        // Keep existing file
        existingFiles.push("articleOfAssociation");
      }

      // Add existing files to formData
      existingFiles.forEach((fileName) => {
        formData.append("existingFiles", fileName);
      });

      await uploadSupplierDocuments(selectedSupplier?.id, formData);

      // Use the existing uploadSupplierDocuments function

      // Success - refetch suppliers
      await getSuppliers();

      setError("");
    } catch (error) {
      console.error("Error uploading supplier files:", error);
      setError("errorUploadingFiles");
    } finally {
      setLoadingModal(false);
    }
  };

  const handleSkipUpload = () => {
    handleCompleteCreation();
  };

  const handleCompleteCreation = () => {
    setSupplierModal(false);
    setCreationStep(1);
    setNewlyCreatedSupplierId(null);
    setUploadFiles({
      supplierCertificate: null,
      articlesOfAssociation: null,
    });
    setAddBankAccountFields(false);
    setError("");
    getSuppliers();
  };

  const handleSupplierFileChange = (file, fieldName) => {
    setSupplierFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    if (error) setError("");
  };

  const handleDeleteSupplierFile = (fieldName) => {
    setSupplierFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
  };

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}


      >

        <div className="users-title-section">
          <h4 className="page--title">
            {t("suppliers", { defaultValue: "Suppliers" })}
          </h4>
          {userData?.userId ? (
            <CustomButton
              title={t("addSupplier")}
              variant="blank"
              icon="addP"
              iconSize={20}
              onClick={handleShowSupplierModal}
            />
          ) : null}
        </div>

        <CustomInput
          name="supplierName"
          value={searchTerm}
          placeholder={searchTerm ? "" : t("search")}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {loading ? (
          <Loader />
        ) : filteredSuppliers.length === 0 ? (
          <p>{t("noSuppliers")}.</p>
        ) : (
          <ul className="users-list" style={{ direction: currentDirection }}>
            {filteredSuppliers.map((supplier) => (
              <li
                className="user-list-item"
                key={supplier.id}
                onClick={() => openUpdateSupplierModal(supplier)}
                style={{ cursor: "pointer" }}
              >
                <p className="user-username">{supplier.name}</p>
                {supplier.company && (
                 <p className="user-email">{supplier.company}</p>
                )}
              </li>
            ))}
          </ul>
        )}

        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              icon={currentDirection === "ltr" ? "previous" : "next"}
              iconSize={15}
            />
            <span className="pagination-location">
              {t("page")}: {currentPage} - {totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              icon={currentDirection === "ltr" ? "next" : "previous"}
              iconSize={15}
              reverseIcon
            />
          </div>

          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalNumber")}: {totalRecords}
            </span>
          </div>
        </section>

        {updateSupplierModal && (
          <Modal
            title={isEditing ? t("editSupplier") : t("supplier")}
            onClose={isEditing ? handleAttemptCancelUpdate : handleCancel}
            footer={
              <>
                {loadingModal ? <Loader /> : null}

                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        onClick={handleUpdateSupplier}
                        title={t("confirm")}
                        icon="save"
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleAttemptCancelUpdate}
                        variant="blank"
                        icon="close"
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon="edit"
                        iconSize={18}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div className="o-column o-column--gap-size_100">
              {isEditing && error ? (
                <ErrorBox>
                  {t(error)}
                  {duplicateAccountNumber ? (
                    <span className="bold-text">{duplicateAccountNumber}</span>
                  ) : null}
                </ErrorBox>
              ) : null}
              {isEditing ? (
                // FROM SUPPLIER NAME TO SUPPLIER COMPANY
                <div className="edit-user-entity">
                  <span>{t("supplierCompany")}: </span>
                  <CustomInput
                    name="supplierName"
                    value={updateSupplierFields.supplierName || ""}
                    onChange={(e) => handleUpdateInputChange(e, "supplierName")}
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("supplierCompany")}:</p>
                  <span>{selectedSupplier?.name}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("supplierName")}: </span>
                  <CustomInput
                    name="supplierCompany"
                    value={updateSupplierFields.supplierCompany || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierCompany")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("supplierName")}:</p>
                  <span>{selectedSupplier?.company}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("businessSector")}: </span>
                  <CustomInput
                    name="supplierBusinessSector"
                    value={updateSupplierFields.supplierBusinessSector || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierBusinessSector")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("businessSector")}:</p>
                  <span>{selectedSupplier?.businessSector || "---"}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("website")}: </span>
                  <CustomInput
                    name="supplierWebsite"
                    value={updateSupplierFields?.supplierWebsite || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierWebsite")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("website")}:</p>
                  <span>{selectedSupplier?.website}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("email")}: </span>
                  <CustomInput
                    name="supplierEmail"
                    value={updateSupplierFields?.supplierEmail || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierEmail")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("email")}:</p>
                  <span>{selectedSupplier?.email}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("address")}: </span>
                  <CustomInput
                    name="supplierAddress"
                    value={updateSupplierFields?.supplierAddress || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierAddress")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("address")}:</p>
                  <span>{selectedSupplier?.address}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("country")}: </span>
                  <CustomInput
                    name="supplierCountry"
                    value={updateSupplierFields.supplierCountry || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierCountry")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("country")}:</p>
                  <span>{selectedSupplier?.country}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("city")}: </span>
                  <CustomInput
                    name="supplierCity"
                    value={updateSupplierFields.supplierCity || ""}
                    onChange={(e) => handleUpdateInputChange(e, "supplierCity")}
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("city")}:</p>
                  <span>{selectedSupplier?.city}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("phoneNumber")}: </span>
                  <CustomInput
                    name="supplierPhoneNumber"
                    value={updateSupplierFields.supplierPhoneNumber || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierPhoneNumber")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("phoneNumber")}:</p>
                  <span>{selectedSupplier?.phoneNumber}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("countryCode")}: </span>
                  <CustomInput
                    name="supplierCountryCode"
                    value={updateSupplierFields.supplierCountryCode || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierCountryCode")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("countryCode")}:</p>
                  <span>{selectedSupplier?.countryCode}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("typeOfGoods")}: </span>
                  <CustomInput
                    name="supplierGoodsType"
                    value={updateSupplierFields.supplierGoodsType || ""}
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierGoodsType")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("typeOfGoods")}:</p>
                  <span>{selectedSupplier?.typeOfGoods}</span>
                </div>
              )}
              {isEditing ? (
                <div className="edit-user-entity">
                  <span>{t("relationWithTheSupplier")}: </span>
                  <CustomInput
                    name="supplierRelationWithSupplier"
                    value={
                      updateSupplierFields.supplierRelationWithSupplier || ""
                    }
                    onChange={(e) =>
                      handleUpdateInputChange(e, "supplierRelationWithSupplier")
                    }
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("relationWithTheSupplier")}:</p>
                  <span>{selectedSupplier?.relationWithSupplier}</span>
                </div>
              )}
              <div className="view-user-entity">
                <p>{t("supplierCurrency")}:</p>
                <span>{selectedSupplier?.currency}</span>
              </div>

              {/* Bank Account section */}
              {isEditing ? (
                <BankAccountSection
                  sendDataToParent={handleEditBankAccount}
                  data={selectedSupplier?.bankAccount}
                  deleteUrl="removeBankAccountFromSupplier"
                  refetchFunction={getSuppliers}
                  isIraqi={false}
                />
              ) : (
                <>
                  {selectedSupplier?.bankAccount?.length ? (
                    <>
                      <SectionDivider title="bankAccountInfo" />
                      <section className="o-column o-column--gap-size_500">
                        {selectedSupplier?.bankAccount?.map((bankAccount) => (
                          <div>
                            <div className="view-user-entity">
                              <p>{t("bankName")}:</p>
                              <span>{bankAccount?.bank?.name}</span>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("branch")}:</p>
                              <span>{bankAccount?.branch?.address}</span>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("accountNumber")}:</p>
                              <span>{bankAccount?.accountNumber}</span>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("accountCurrency")}:</p>
                              <span>{bankAccount?.accountCurrency}</span>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("SWIFT")}:</p>
                              <span>{bankAccount?.swiftCode}</span>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("IBAN")}:</p>
                              <span>{bankAccount?.iban}</span>
                            </div>
                          </div>
                        ))}
                      </section>
                    </>
                  ) : null}
                </>
              )}

              {/* supplier documents section */}
              {isEditing ? (
                <>
                  <SectionDivider title="supplierDocuments" />
                  <div className="o-column o-column--gap-size_300">
                    <div className="o-column u-margin--top_200">
                      <SectionDivider
                        title={t("supplierCertificate")}
                        noMargin={true}
                      />
                      <UploadFiles
                        fieldName="supplierCertificate"
                        file={
                          supplierFiles.supplierCertificate
                            ? supplierFiles.supplierCertificate
                            : null
                        }
                        onFileChange={handleSupplierFileChange}
                        onDeleteFile={handleDeleteSupplierFile}
                        existingFileName={
                          supplierFiles.supplierCertificate?.name ||
                          supplierFiles.supplierCertificate?.path
                            ?.split("/")
                            .pop()
                        }
                      />
                    </div>

                    <div className="o-column u-margin--top_200">
                      <SectionDivider
                        title={t("articlesOfAssociation")}
                        noMargin={true}
                      />
                      <UploadFiles
                        fieldName="articlesOfAssociation"
                        file={
                          supplierFiles.articlesOfAssociation
                            ? supplierFiles.articlesOfAssociation
                            : null
                        }
                        onFileChange={handleSupplierFileChange}
                        onDeleteFile={handleDeleteSupplierFile}
                        existingFileName={
                          supplierFiles.articlesOfAssociation?.name ||
                          supplierFiles.articlesOfAssociation?.path
                            ?.split("/")
                            .pop()
                        }
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {selectedSupplier?.documents?.length ? (
                    <>
                      <SectionDivider title="supplierDocuments" />
                      <section className="o-column o-column--gap-size_500">
                        {selectedSupplier.documents.map((doc, index) => (
                          <div key={index}>
                            <p className="bold-text">
                              {doc.name === "supplierCertificate"
                                ? t("supplierCertificate")
                                : t("articlesOfAssociation")}
                            </p>
                            <PDFViewer
                              fileId={doc.S3Key}
                              filePath={doc.path}
                              mode="show"
                              fromFile={true}
                            />
                          </div>
                        ))}
                      </section>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </Modal>
        )}

        {showUpdateAreYouSure && isEditing ? (
          <AreYouSureModal
            onClose={() => setShowUpdateAreYouSure(false)}
            onConfirm={() => handleCancel()}
            confirmIconType="whiteCheck"
            confirmTitle="close"
            customMessage={t("areYouSureCancelUpdate")}
          />
        ) : null}

        {supplierModal && (
          <Modal
            title={
              creationStep === 1
                ? t("addSupplier")
                : t("uploadSupplierDocuments")
            }
            onClose={() => handleAttemptCancel()}
            error={creationStep === 1 ? t(error) : ""}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className={classes.actions}>
                  {creationStep === 1 ? (
                    <>
                      <CustomButton
                        onClick={handleAddSupplier}
                        title={t("confirm")}
                        icon="save"
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={() => handleAttemptCancel()}
                        variant="blank"
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        title={t("save")}
                        onClick={handleUploadFiles}
                        icon="save"
                        iconSize={16}
                        disabled={
                          loadingModal ||
                          (!uploadFiles.supplierCertificate &&
                            !uploadFiles.articlesOfAssociation)
                        }
                      />

                      <CustomButton
                        title={t("skip")}
                        onClick={handleSkipUpload}
                        variant="blank"
                        icon="skip"
                        iconSize={24}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            {creationStep === 1 ? (
              // Step 1: Existing supplier data entry form
              <div className={classes.flexableForm}>
                {error ? <ErrorBox>{t(error)}</ErrorBox> : null}
                {/* All your existing form sections remain exactly the same */}
                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("supplierCompany")}: </span>
                    <CustomInput
                      name="supplierName"
                      value={supplierFields?.supplierName}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                      errorText={emptyInputField && "noSupplierName"}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("supplierName")}: </span>
                    <CustomInput
                      name="supplierCompany"
                      value={supplierFields?.supplierCompany}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>
                      {t("supplierCurrency")}:{" "}
                    </span>
                    <PortalDropdown
                      options={currencies}
                      selected={supplierCurrency}
                      setSelected={setSupplierCurrency}
                      thick
                      flex
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("businessSector")}: </span>
                    <CustomInput
                      name="supplierBusinessSector"
                      value={supplierFields?.supplierBusinessSector}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("typeOfGoods")}: </span>
                    <CustomInput
                      name="supplierGoodsType"
                      value={supplierFields?.supplierGoodsType}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("address")}: </span>
                    <CustomInput
                      name="supplierAddress"
                      value={supplierFields?.supplierAddress}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                      errorText={
                        error === "noSupplierAddress" ? t(error) : null
                      }
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("country")}: </span>
                    <CustomInput
                      name="supplierCountry"
                      value={supplierFields?.supplierCountry}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                      errorText={
                        error === "noSupplierCountry" ? t(error) : null
                      }
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("countryCode")}: </span>
                    <CustomInput
                      name="supplierCountryCode"
                      value={supplierFields?.supplierCountryCode}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                      errorText={
                        error === "noSupplierCountry" ? t(error) : null
                      }
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("city")}: </span>
                    <CustomInput
                      name="supplierCity"
                      value={supplierFields?.supplierCity}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("phoneNumber")}: </span>
                    <CustomInput
                      name="supplierPhoneNumber"
                      value={supplierFields?.supplierPhoneNumber}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("website")}: </span>
                    <CustomInput
                      name="supplierWebsite"
                      value={supplierFields?.supplierWebsite}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>{t("email")}: </span>
                    <CustomInput
                      name="supplierEmail"
                      value={supplierFields?.supplierEmail}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                <section>
                  <div className={classes.inputContainer}>
                    <span className={spanStyles}>
                      {t("relationWithTheSupplier")}:{" "}
                    </span>
                    <CustomInput
                      name="supplierRelationWithSupplier"
                      value={supplierFields?.supplierRelationWithSupplier}
                      onChange={(e) => handleInputChange(e, "supplier")}
                      flex={true}
                    />
                  </div>
                </section>

                {addBankAccountFields ? (
                  <BankAccountSection
                    sendDataToParent={handleBankAccount}
                    isIraqi={false}
                  />
                ) : (
                  <>
                    <div className="o-column o-column--vertical-align_end">
                      <CustomButton
                        onClick={handleAddBankAccount}
                        variant="blank"
                        icon="addP"
                        title={t("addBankAccount")}
                        disabled={addBankAccountFields}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              // Step 2: File Upload
              <div className="o-column o-column--gap-size_300">
                {error && <ErrorBox>{t(error)}</ErrorBox>}
                <div className="o-row o-row--horizontal-align_center">
                  <ConfirmationBox>
                    <p>{t("supplierWasAddedSuccessfully")}</p>
                  </ConfirmationBox>
                </div>

                <div className="o-column u-margin--top_200">
                  <SectionDivider
                    title={t("supplierCertificate")}
                    noMargin={true}
                  />
                  <UploadFiles
                    fieldName="supplierCertificate"
                    file={uploadFiles.supplierCertificate}
                    onFileChange={handleFileChange}
                    onDeleteFile={handleDeleteFile}
                  />
                </div>

                <div className="o-column u-margin--top_200">
                  <SectionDivider
                    title={t("articlesOfAssociation")}
                    noMargin={true}
                  />
                  <UploadFiles
                    fieldName="articlesOfAssociation"
                    file={uploadFiles.articlesOfAssociation}
                    onFileChange={handleFileChange}
                    onDeleteFile={handleDeleteFile}
                  />
                </div>
              </div>
            )}
          </Modal>
        )}

        {showAreYouSure ? (
          <AreYouSureModal
            confirmIconType="whiteCheck"
            confirmTitle="close"
            onClose={() => setShowAreYouSure(false)}
            onConfirm={() => handleCancel("newSupplier")}
          />
        ) : null}
      </PageWrapper>
    </div>
  );
}
